<template>
    <div class="page-container">
        <subscription-banner></subscription-banner>
        <!-- Loader -->
        <div v-if="loading" class="loader-container">
            <div class="loader"></div>
        </div>

        <!-- Top Bar -->
        <div class="top-bar">
            <div class="top-bar-left">
                <h2 class="page-title">
                    <i class="tim-icons icon-tv-2"></i>
                    {{ $t('esirs.title') }}
                </h2>
                <span class="count-badge">{{ esirs.length }}</span>
            </div>
            <div class="top-bar-right">
                <base-button type="primary" @click="openForm()" class="action-btn">
                    <i class="tim-icons icon-simple-add"></i> {{ $t('esirs.add') }}
                </base-button>
            </div>
        </div>

        <!-- Filters -->
        <div class="filter-bar" v-if="locations.length > 0">
            <label class="filter-label">{{ $t('esirs.table.location') }}:</label>
            <select v-model="selectedLocationId" class="filter-select" @change="filterEsirs">
                <option value="">{{ $t('invoices.filters.all') }}</option>
                <option v-for="l in locations" :key="l.id" :value="l.id">
                    {{ l.name }} {{ l.tin ? '(' + l.tin + ')' : '' }}
                </option>
            </select>
            <base-button v-if="selectedLocationId" type="default" size="sm" @click="selectedLocationId = ''; filterEsirs()" class="clear-btn">
                <i class="tim-icons icon-simple-remove"></i> {{ $t('pos.receipt.clear') }}
            </base-button>
        </div>

        <!-- Table -->
        <div class="table-section">
            <div class="table-toolbar">
                <base-input v-model="search" :placeholder="$t('common.search') + '...'" class="search-input">
                </base-input>
            </div>

            <div class="table-container">
                <table class="custom-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{{ $t('esirs.table.location') }}</th>
                            <th>{{ $t('esirs.table.esirCode') }}</th>
                            <!-- <th>{{ $t('esirs.table.posNumber') }}</th> -->
                            <th>{{ $t('esirs.table.locationName') }}</th>
                            <th>{{ $t('esirs.table.address') }}</th>
                            <th>{{ $t('esirs.table.district') }}</th>
                            <th>{{ $t('esirs.table.cert') }}</th>
                            <th>{{ $t('esirs.table.items') }}</th>
                            <th>{{ $t('common.actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(esir, index) in filteredEsirs" :key="esir.id">
                            <td>{{ index + 1 }}</td>
                            <td>
                                <span class="location-tag" v-if="esir.location">
                                    {{ esir.location.name }}
                                </span>
                                <span v-else class="no-data">-</span>
                            </td>
                            <td><span class="mono">{{ esir.esirCode || '-' }}</span></td>
                            <!-- <td><span class="mono">{{ esir.posNumber || '-' }}</span></td> -->
                            <td class="name-cell">{{ esir.locationName || '-' }}</td>
                            <td>{{ esir.address || '-' }}</td>
                            <td>{{ esir.district || '-' }}</td>
                            <td>
                                <span v-if="esir.hasCertificate" class="cert-ok-tag" :title="$t('pos.certOk')">
                                    <i class="tim-icons icon-check-2"></i>
                                </span>
                                <span v-else class="cert-no-tag" title="Nema sertifikata">
                                    <i class="tim-icons icon-simple-remove"></i>
                                </span>
                            </td>
                            <td>
                                <span class="items-tag" @click="openItems(esir)">
                                    <i class="tim-icons icon-bag-16"></i> {{ $t('esirs.table.items') }}
                                </span>
                            </td>
                            <td>
                                <div class="action-buttons-cell">
                                    <base-button type="primary" icon size="sm" class="btn-link"
                                        @click="openPOS(esir)" :title="$t('esirs.openPos')">
                                        <i class="tim-icons icon-cart"></i>
                                    </base-button>
                                    <base-button type="success" icon size="sm" class="btn-link"
                                        @click="openForm(esir)" :title="$t('common.edit')">
                                        <i class="tim-icons icon-pencil"></i>
                                    </base-button>
                                    <base-button type="danger" icon size="sm" class="btn-link"
                                        @click="confirmDelete(esir)" :title="$t('common.delete')">
                                        <i class="tim-icons icon-trash-simple"></i>
                                    </base-button>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="filteredEsirs.length === 0">
                            <td colspan="10" class="empty-row">
                                <i class="tim-icons icon-zoom-split"></i>
                                <p>{{ search ? $t('common.noData') : $t('common.noData') }}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ==================== ESIR Add/Edit Modal ==================== -->
        <transition name="modal-fade">
            <div v-if="showFormModal" class="modal-overlay" @click="showFormModal = false">
                <div class="modal-container" @click.stop>
                    <div class="modal-header">
                        <h3>
                            <i class="tim-icons icon-tv-2"></i>
                            {{ form.id ? $t('common.edit') + ' ESIR' : $t('esirs.add') }}
                        </h3>
                        <button @click="showFormModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div class="form-grid">
                            <div class="form-group full-width">
                                <label class="form-label">{{ $t('esirs.table.location') }} *</label>
                                <select v-model="form.locationId" class="form-select">
                                    <option value="">{{ $t('esirs.table.location') }}</option>
                                    <option v-for="l in locations" :key="l.id" :value="l.id">
                                        {{ l.name }} {{ l.tin ? '(' + l.tin + ')' : '' }}
                                    </option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.form.esirCode') }}</label>
                                <base-input v-model="form.esirCode" placeholder="HYQ56XFT"></base-input>
                            </div>
                            <!-- <div class="form-group">
                                <label class="form-label">{{ $t('esirs.form.posNumber') }}</label>
                                <base-input v-model="form.posNumber" placeholder="1029/1.01"></base-input>
                            </div> -->
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.form.locationName') }}</label>
                                <base-input v-model="form.locationName" placeholder="Prodajno mesto"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.form.address') }}</label>
                                <base-input v-model="form.address" placeholder="Ulica i broj"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.form.district') }}</label>
                                <base-input v-model="form.district" :placeholder="$t('esirs.form.district')"></base-input>
                            </div>
                        </div>

                        <!-- VPFR Sekcija -->
                        <div class="section-divider">
                            <span>{{ $t('esirs.form.vpfr') }}</span>
                        </div>
                        <div class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.form.vpfrPac') }}</label>
                                <base-input v-model="form.vpfrPac" :placeholder="$t('esirs.form.vpfrPac')"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.form.vpfrPassword') }}</label>
                                <base-input v-model="form.vpfrPassword" type="password" placeholder="Password za sertifikat"></base-input>
                            </div>
                        </div>

                        <!-- Sertifikat sekcija (samo za edit) -->
                        <div v-if="form.id" class="cert-section">
                            <div class="cert-status">
                                <div v-if="form.hasCertificate" class="cert-uploaded">
                                    <i class="tim-icons icon-check-2"></i>
                                    <span>{{ $t('esirs.form.certificate') }}: <strong>{{ form.certificateFileName }}</strong></span>
                                    <base-button type="danger" size="sm" class="btn-link cert-delete-btn"
                                        @click="deleteCertificate()" :title="$t('esirs.form.deleteCert')">
                                        <i class="tim-icons icon-trash-simple"></i>
                                    </base-button>
                                </div>
                                <div v-else class="cert-missing">
                                    <i class="tim-icons icon-alert-circle-exc"></i>
                                    <span>{{ $t('esirs.form.noCert') }}</span>
                                </div>
                            </div>

                            <div class="cert-upload-row">
                                <label class="cert-upload-btn">
                                    <i class="tim-icons icon-cloud-upload-94"></i>
                                    {{ form.hasCertificate ? $t('esirs.form.uploadCert') : $t('esirs.form.uploadCert') }}
                                    <input type="file" accept=".p12,.pfx" @change="uploadCertificate($event)" hidden>
                                </label>
                                <span v-if="certUploading" class="cert-uploading">
                                    <i class="tim-icons icon-refresh-02 spin"></i> {{ $t('common.loading') }}
                                </span>
                            </div>

                            <base-button
                                v-if="form.hasCertificate && form.vpfrPac && form.vpfrPassword"
                                type="info" size="sm" class="verify-btn"
                                @click="verifyCertificate()"
                                :disabled="certVerifying">
                                <i class="tim-icons icon-lock-circle" v-if="!certVerifying"></i>
                                <i class="tim-icons icon-refresh-02 spin" v-else></i>
                                {{ certVerifying ? $t('common.loading') : $t('esirs.form.verifyCert') }}
                            </base-button>
                        </div>
                        <div v-else class="cert-section-hint">
                            <i class="tim-icons icon-alert-circle-exc"></i>
                            {{ $t('common.save') }} ESIR first.
                        </div>
                    </div>
                    <div class="modal-footer">
                        <base-button type="primary" @click="saveEsir()">
                            <i class="tim-icons icon-check-2"></i> {{ $t('common.save') }}
                        </base-button>
                        <base-button type="default" @click="showFormModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ==================== ESIR Delete Modal ==================== -->
        <transition name="modal-fade">
            <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
                <div class="modal-container modal-sm" @click.stop>
                    <div class="modal-header danger-header">
                        <h3><i class="tim-icons icon-alert-circle-exc"></i> Brisanje</h3>
                        <button @click="showDeleteModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <p class="confirm-text">
                            {{ $t('common.delete') }} ESIR
                            <strong>"{{ deleteTarget ? (deleteTarget.locationName || deleteTarget.esirCode || deleteTarget.id) : '' }}"</strong>?
                        </p>
                    </div>
                    <div class="modal-footer">
                        <base-button type="danger" @click="deleteEsir()">
                            <i class="tim-icons icon-trash-simple"></i> {{ $t('common.delete') }}
                        </base-button>
                        <base-button type="default" @click="showDeleteModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ==================== ARTIKLI Modal ==================== -->
        <transition name="modal-fade">
            <div v-if="showItemsModal" class="modal-overlay" @click="showItemsModal = false">
                <div class="modal-container modal-lg" @click.stop>
                    <div class="modal-header items-header">
                        <h3>
                            <i class="tim-icons icon-bag-16"></i>
                            {{ $t('esirs.items.title') }}: {{ itemsEsir ? (itemsEsir.locationName || itemsEsir.esirCode) : '' }}
                        </h3>
                        <button @click="showItemsModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body" style="padding: 0;">
                        <!-- Items Toolbar -->
                        <div class="items-toolbar">
                            <base-input v-model="itemSearch" :placeholder="$t('common.search') + '...'" class="search-input">
                            </base-input>
                            <base-button type="primary" size="sm" @click="openItemForm()" class="action-btn">
                                <i class="tim-icons icon-simple-add"></i> {{ $t('esirs.items.add') }}
                            </base-button>
                        </div>

                        <!-- Items Table -->
                        <div class="table-container">
                            <table class="custom-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{{ $t('esirs.items.name') }}</th>
                                        <th>{{ $t('esirs.items.unit') }}</th>
                                        <th>{{ $t('esirs.items.gtin') }}</th>
                                        <th>{{ $t('esirs.items.taxLabels') }}</th>
                                        <th>{{ $t('esirs.items.price') }}</th>
                                        <th>{{ $t('common.actions') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in filteredItems" :key="item.id">
                                        <td>{{ index + 1 }}</td>
                                        <td class="name-cell">{{ item.name }}</td>
                                        <td>{{ item.unit || '-' }}</td>
                                        <td><span class="mono" v-if="item.gtin">{{ item.gtin }}</span><span v-else class="no-data">-</span></td>
                                        <td>
                                            <span v-if="item.taxLabels" class="tax-tag">{{ item.taxLabels }}</span>
                                            <span v-else class="no-data">-</span>
                                        </td>
                                        <td>
                                            <span class="mono" v-if="item.price">{{ formatPrice(item.price) }}</span>
                                            <span v-else class="no-data">-</span>
                                        </td>
                                        <td>
                                            <div class="action-buttons-cell">
                                                <base-button type="success" icon size="sm" class="btn-link"
                                                    @click="openItemForm(item)" :title="$t('common.edit')">
                                                    <i class="tim-icons icon-pencil"></i>
                                                </base-button>
                                                <base-button type="danger" icon size="sm" class="btn-link"
                                                    @click="confirmDeleteItem(item)" :title="$t('common.delete')">
                                                    <i class="tim-icons icon-trash-simple"></i>
                                                </base-button>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="filteredItems.length === 0">
                                        <td colspan="7" class="empty-row">
                                            <i class="tim-icons icon-bag-16"></i>
                                            <p>{{ itemSearch ? $t('common.noData') : $t('common.noData') }}</p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="items-footer-info">
                            Ukupno artikala: <strong>{{ items.length }}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ==================== Artikal Add/Edit Modal ==================== -->
        <transition name="modal-fade">
            <div v-if="showItemFormModal" class="modal-overlay" @click="showItemFormModal = false">
                <div class="modal-container modal-sm" @click.stop>
                    <div class="modal-header items-header">
                        <h3>
                            <i class="tim-icons icon-bag-16"></i>
                            {{ itemForm.id ? $t('common.edit') : $t('esirs.items.add') }}
                        </h3>
                        <button @click="showItemFormModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div class="form-grid-single">
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.items.name') }} *</label>
                                <base-input v-model="itemForm.name" :placeholder="$t('esirs.items.name')"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.items.unit') }}</label>
                                <select v-model="itemForm.unit" class="form-select">
                                    <option value="">—</option>
                                    <option value="kom">{{ $i18n.locale === 'sr' ? 'kom' : 'pcs' }}</option>
                                    <option value="kg">kg</option>
                                    <option value="lit">{{ $i18n.locale === 'sr' ? 'lit' : 'lit' }}</option>
                                    <option value="m">m</option>
                                    <option value="m2">m²</option>
                                    <option value="m3">m³</option>
                                    <option value="h">h (sat)</option>
                                    <option value="par">{{ $i18n.locale === 'sr' ? 'par' : 'pair' }}</option>
                                    <option value="pak">{{ $i18n.locale === 'sr' ? 'pak' : 'pack' }}</option>
                                    <option value="kut">{{ $i18n.locale === 'sr' ? 'kut' : 'box' }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.items.gtin') }}</label>
                                <base-input v-model="itemForm.gtin" placeholder="8600000000000"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.items.taxLabels') }}</label>
                                <select v-model="itemForm.taxLabels" class="form-select">
                                    <option value="">—</option>
                                    <option value="Ђ">Ђ - 20%</option>
                                    <option value="Е">Е - 10%</option>
                                    <option value="Г">Г - 0% (exempt)</option>
                                    <option value="А">А - 0% (non-taxable)</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('esirs.items.price') }} (RSD)</label>
                                <base-input v-model="itemForm.price" type="number" placeholder="0.00"></base-input>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <base-button type="primary" @click="saveItem()">
                            <i class="tim-icons icon-check-2"></i> {{ $t('common.save') }}
                        </base-button>
                        <base-button type="default" @click="showItemFormModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ==================== Artikal Delete Modal ==================== -->
        <transition name="modal-fade">
            <div v-if="showItemDeleteModal" class="modal-overlay" @click="showItemDeleteModal = false">
                <div class="modal-container modal-sm" @click.stop>
                    <div class="modal-header danger-header">
                        <h3><i class="tim-icons icon-alert-circle-exc"></i> {{ $t('common.delete') }}</h3>
                        <button @click="showItemDeleteModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <p class="confirm-text">
                            {{ $t('common.delete') }}?
                            <strong>"{{ itemDeleteTarget ? itemDeleteTarget.name : '' }}"</strong>?
                        </p>
                    </div>
                    <div class="modal-footer">
                        <base-button type="danger" @click="deleteItem()">
                            <i class="tim-icons icon-trash-simple"></i> {{ $t('common.delete') }}
                        </base-button>
                        <base-button type="default" @click="showItemDeleteModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Toast -->
        <transition name="notif-fade">
            <div v-if="toast.message" :class="['toast-notif', toast.type]" @click="toast.message = ''">
                <i :class="toast.type === 'success' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc'"></i>
                <span>{{ toast.message }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from '@/util/axiosinstance'
import SubscriptionBanner from './SubscriptionBanner.vue'

export default {
    name: 'FiscalEsirs',
    components: { SubscriptionBanner },
    data() {
        return {
            loading: false,
            esirs: [],
            locations: [],
            search: '',
            selectedLocationId: '',
            // ESIR modals
            showFormModal: false,
            showDeleteModal: false,
            deleteTarget: null,
            form: {},
            certUploading: false,
            certVerifying: false,
            // Items
            showItemsModal: false,
            itemsEsir: null,
            items: [],
            itemSearch: '',
            // Item form
            showItemFormModal: false,
            itemForm: {},
            // Item delete
            showItemDeleteModal: false,
            itemDeleteTarget: null,
            //
            toast: { message: '', type: 'success' }
        }
    },
    computed: {
        filteredEsirs() {
            if (!this.search) return this.esirs
            const s = this.search.toLowerCase()
            return this.esirs.filter(e =>
                (e.esirCode && e.esirCode.toLowerCase().includes(s)) ||
                (e.posNumber && e.posNumber.toLowerCase().includes(s)) ||
                (e.locationName && e.locationName.toLowerCase().includes(s)) ||
                (e.location && e.location.name && e.location.name.toLowerCase().includes(s))
            )
        },
        filteredItems() {
            if (!this.itemSearch) return this.items
            const s = this.itemSearch.toLowerCase()
            return this.items.filter(i =>
                (i.name && i.name.toLowerCase().includes(s)) ||
                (i.gtin && i.gtin.toLowerCase().includes(s))
            )
        }
    },
    created() {
        this.getLocations()
        if (this.$route.query.locationId) {
            this.selectedLocationId = this.$route.query.locationId
            this.getEsirsByLocation(this.selectedLocationId)
        } else {
            this.getEsirs()
        }
    },
    methods: {
        // ===================== ESIR CRUD =====================
        getLocations() {
            axios.get('/api/v1/locations').then(res => {
                this.locations = res.data
            })
        },

        getEsirs() {
            this.loading = true
            axios.get('/api/v1/esirs').then(res => {
                this.loading = false
                this.esirs = res.data
            }).catch(err => {
                this.loading = false
                this.showToast(this.$t('pos.result.error'), 'error')
            })
        },

        getEsirsByLocation(locationId) {
            this.loading = true
            axios.get('/api/v1/esirs/location/' + locationId).then(res => {
                this.loading = false
                this.esirs = res.data
            }).catch(err => {
                this.loading = false
                this.showToast(this.$t('pos.result.error'), 'error')
            })
        },

        filterEsirs() {
            if (this.selectedLocationId) {
                this.getEsirsByLocation(this.selectedLocationId)
            } else {
                this.getEsirs()
            }
        },

        openForm(esir) {
            if (esir) {
                this.form = {
                    id: esir.id,
                    locationId: esir.location ? esir.location.id : '',
                    esirCode: esir.esirCode,
                    posNumber: esir.posNumber,
                    locationName: esir.locationName,
                    address: esir.address,
                    district: esir.district,
                    mrc: esir.mrc,
                    vpfrPac: esir.vpfrPac || '',
                    vpfrPassword: esir.vpfrPassword || '',
                    hasCertificate: esir.hasCertificate || false,
                    certificateFileName: esir.certificateFileName || ''
                }
            } else {
                this.form = {
                    locationId: this.selectedLocationId || '',
                    esirCode: '', posNumber: '', locationName: '',
                    address: '', district: '', mrc: '',
                    vpfrPac: '', vpfrPassword: '',
                    hasCertificate: false, certificateFileName: ''
                }
            }
            this.showFormModal = true
        },

        saveEsir() {
            if (!this.form.locationId) {
                this.showToast(this.$t('esirs.table.location'), 'error')
                return
            }
            this.loading = true
            const isEdit = !!this.form.id
            const request = isEdit
                ? axios.put('/api/v1/esir/' + this.form.id, this.form)
                : axios.post('/api/v1/esir', this.form)

            request.then(res => {
                this.loading = false
                this.showFormModal = false
                this.showToast(isEdit ? this.$t('common.save') : this.$t('common.save'), 'success')
                this.filterEsirs()
            }).catch(err => {
                this.loading = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        confirmDelete(esir) {
            this.deleteTarget = esir
            this.showDeleteModal = true
        },

        openPOS(esir) {
            this.$router.push('/fiscal/pos?esirId=' + esir.id)
        },

        deleteEsir() {
            this.loading = true
            axios.delete('/api/v1/esir/' + this.deleteTarget.id).then(res => {
                this.loading = false
                this.showDeleteModal = false
                this.showToast(this.$t('common.delete'), 'success')
                this.filterEsirs()
            }).catch(err => {
                this.loading = false
                this.showDeleteModal = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        // ===================== CERTIFICATES =====================
        uploadCertificate(event) {
            const file = event.target.files[0]
            if (!file) return

            this.certUploading = true
            const formData = new FormData()
            formData.append('certificate', file)

            axios.post('/api/v1/esir/' + this.form.id + '/certificate', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(res => {
                this.certUploading = false
                this.form.hasCertificate = true
                this.form.certificateFileName = res.data.fileName
                this.showToast('Sertifikat uploadovan', 'success')
                this.filterEsirs()
            }).catch(err => {
                this.certUploading = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })

            // Reset input
            event.target.value = ''
        },

        deleteCertificate() {
            if (!confirm(this.$t('esirs.form.deleteCert') + '?')) return

            axios.delete('/api/v1/esir/' + this.form.id + '/certificate').then(res => {
                this.form.hasCertificate = false
                this.form.certificateFileName = ''
                this.showToast('Sertifikat obrisan', 'success')
                this.filterEsirs()
            }).catch(err => {
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        verifyCertificate() {
            this.certVerifying = true
            axios.post('/api/v1/esir/' + this.form.id + '/certificate/verify').then(res => {
                this.certVerifying = false
                const d = res.data
                if (d.status === 'OK' && d.isValid) {
                    let msg = 'Sertifikat validan'
                    if (d.commonName) msg += ' | ' + d.commonName
                    if (d.jib) msg += ' | JIB: ' + d.jib
                    if (d.notAfter) msg += ' | Valid until: ' + d.notAfter
                    this.showToast(msg, 'success')
                } else {
                    this.showToast(d.message || 'Sertifikat nije validan', 'error')
                }
            }).catch(err => {
                this.certVerifying = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        // ===================== ITEMS =====================
        openItems(esir) {
            this.itemsEsir = esir
            this.items = []
            this.itemSearch = ''
            this.showItemsModal = true
            this.loadItems()
        },

        loadItems() {
            if (!this.itemsEsir) return
            axios.get('/api/v1/esirItems/' + this.itemsEsir.id).then(res => {
                this.items = res.data
            }).catch(err => {
                this.showToast(this.$t('pos.result.error'), 'error')
            })
        },

        openItemForm(item) {
            if (item) {
                this.itemForm = {
                    id: item.id,
                    name: item.name,
                    unit: item.unit || '',
                    gtin: item.gtin || '',
                    taxLabels: item.taxLabels || '',
                    price: item.price || ''
                }
            } else {
                this.itemForm = {
                    name: '', unit: '', gtin: '', taxLabels: '', price: ''
                }
            }
            this.showItemFormModal = true
        },

        saveItem() {
            if (!this.itemForm.name || !this.itemForm.name.trim()) {
                this.showToast(this.$t('esirs.items.name') + ' - required', 'error')
                return
            }

            this.loading = true
            const isEdit = !!this.itemForm.id

            let payload = { ...this.itemForm }
            if (!isEdit) {
                payload.esirId = this.itemsEsir.id
            }

            const request = isEdit
                ? axios.put('/api/v1/item/' + this.itemForm.id, payload)
                : axios.post('/api/v1/item', payload)

            request.then(res => {
                this.loading = false
                this.showItemFormModal = false
                this.showToast(isEdit ? 'Artikal izmenjen' : 'Artikal dodat', 'success')
                this.loadItems()
            }).catch(err => {
                this.loading = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        confirmDeleteItem(item) {
            this.itemDeleteTarget = item
            this.showItemDeleteModal = true
        },

        deleteItem() {
            this.loading = true
            axios.delete('/api/v1/item/' + this.itemDeleteTarget.id).then(res => {
                this.loading = false
                this.showItemDeleteModal = false
                this.showToast('Artikal obrisan', 'success')
                this.loadItems()
            }).catch(err => {
                this.loading = false
                this.showItemDeleteModal = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        // ===================== UTIL =====================
        formatPrice(val) {
            if (!val && val !== 0) return '-'
            return Number(val).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        showToast(message, type) {
            this.toast = { message, type }
            setTimeout(() => { this.toast = { message: '', type: 'success' } }, 3000)
        }
    }
}
</script>

<style scoped>
.page-container { padding: 0; }
.loader-container {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255,255,255,0.7); display: flex;
    align-items: center; justify-content: center; z-index: 9999;
}
.loader {
    width: 40px; height: 40px;
    border: 4px solid #e9ecef; border-top-color: #1a73e8;
    border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.top-bar {
    display: flex; justify-content: space-between;
    align-items: center; margin-bottom: 15px; flex-wrap: wrap; gap: 10px;
}
.top-bar-left { display: flex; align-items: center; gap: 10px; }
.top-bar-right { display: flex; gap: 8px; flex-wrap: wrap; }
.page-title {
    margin: 0; font-size: 22px; font-weight: 700;
    color: #2c3e50; display: flex; align-items: center; gap: 8px;
}
.page-title i { color: #1a73e8; }
.count-badge {
    background: #1a73e8; color: white;
    padding: 2px 10px; border-radius: 12px;
    font-size: 13px; font-weight: 600;
}
.action-btn {
    padding: 8px 16px !important; font-size: 13px !important;
    font-weight: 600 !important; display: flex;
    align-items: center; gap: 6px;
}

/* Filter */
.filter-bar {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 15px; padding: 12px 15px;
    background: #ffffff; border-radius: 10px;
    border: 1px solid #e0e0e0;
}
.filter-label { font-size: 13px; font-weight: 600; color: #495057; white-space: nowrap; }
.filter-select {
    padding: 8px 12px; border: 2px solid #dee2e6; border-radius: 8px;
    background: white; font-size: 14px; color: #2c3e50;
    cursor: pointer; min-width: 250px;
}
.filter-select:focus { outline: none; border-color: #1a73e8; }
.clear-btn { font-size: 12px !important; padding: 6px 10px !important; }

/* Table */
.table-section {
    background: #ffffff; border-radius: 12px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden;
}
.table-toolbar { padding: 15px; border-bottom: 1px solid #e9ecef; }
.search-input { max-width: 350px; margin-bottom: 0; }
.table-container { overflow-x: auto; }
.custom-table { width: 100%; border-collapse: collapse; }
.custom-table th, .custom-table td {
    padding: 12px 15px; text-align: left;
    border-bottom: 1px solid #e9ecef;
    color: #2c3e50; font-size: 14px;
}
.custom-table th {
    background: #f8f9fa; font-weight: 600;
    font-size: 12px; text-transform: uppercase;
    letter-spacing: 0.3px; color: #495057;
}
.custom-table tbody tr:hover { background: #f1f5ff; }
.name-cell { font-weight: 600; color: #2c3e50; }
.mono {
    font-family: monospace; background: #f1f3f5;
    padding: 2px 8px; border-radius: 4px; font-size: 13px;
}
.location-tag {
    background: #e7f3ff; color: #1a73e8;
    padding: 3px 10px; border-radius: 10px;
    font-size: 12px; font-weight: 600;
}
.items-tag {
    background: #f0e6ff; color: #6f42c1;
    padding: 4px 12px; border-radius: 10px;
    font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.2s;
    display: inline-flex; align-items: center; gap: 5px;
}
.items-tag:hover { background: #6f42c1; color: white; }
.items-tag i { font-size: 13px; }
.cert-ok-tag {
    display: inline-flex; align-items: center; justify-content: center;
    width: 26px; height: 26px; border-radius: 50%;
    background: #d4edda; color: #155724; font-size: 13px;
}
.cert-no-tag {
    display: inline-flex; align-items: center; justify-content: center;
    width: 26px; height: 26px; border-radius: 50%;
    background: #f8d7da; color: #721c24; font-size: 11px;
}
.tax-tag {
    background: #fff3cd; color: #856404;
    padding: 2px 8px; border-radius: 6px;
    font-size: 13px; font-weight: 600;
}
.no-data { color: #adb5bd; }
.action-buttons-cell { display: flex; gap: 5px; }
.empty-row { text-align: center; padding: 40px !important; color: #6c757d; }
.empty-row i { font-size: 36px; display: block; margin-bottom: 10px; opacity: 0.4; }
.empty-row p { margin: 0; }

/* Items toolbar */
.items-toolbar {
    padding: 15px; border-bottom: 1px solid #e9ecef;
    display: flex; justify-content: space-between;
    align-items: center; gap: 10px;
}
.items-footer-info {
    padding: 10px 15px; background: #f8f9fa;
    border-top: 1px solid #e9ecef;
    font-size: 13px; color: #6c757d; text-align: right;
}
.items-footer-info strong { color: #2c3e50; }

/* Form single column */
.form-grid-single {
    display: flex; flex-direction: column; gap: 15px;
}

/* Section divider */
.section-divider {
    margin: 20px 0 15px;
    border-bottom: 2px solid #e7f3ff;
    padding-bottom: 6px;
}
.section-divider span {
    font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.5px;
    color: #1a73e8;
}

/* Certificate section */
.cert-section {
    margin-top: 15px; padding: 15px;
    background: #f8f9fa; border-radius: 10px;
    border: 1px solid #e9ecef;
}
.cert-status { margin-bottom: 12px; }
.cert-uploaded {
    display: flex; align-items: center; gap: 8px;
    color: #155724; font-size: 13px;
}
.cert-uploaded i { color: #28a745; font-size: 16px; }
.cert-delete-btn { margin-left: auto; }
.cert-missing {
    display: flex; align-items: center; gap: 8px;
    color: #856404; font-size: 13px;
}
.cert-missing i { color: #ffc107; font-size: 16px; }
.cert-upload-row {
    display: flex; align-items: center; gap: 10px;
}
.cert-upload-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 16px; background: #1a73e8; color: white;
    border-radius: 8px; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all 0.2s;
}
.cert-upload-btn:hover { background: #0d47a1; }
.cert-upload-btn i { font-size: 15px; }
.cert-uploading {
    font-size: 13px; color: #6c757d;
    display: flex; align-items: center; gap: 5px;
}
.verify-btn {
    margin-top: 12px;
    display: flex !important; align-items: center; gap: 6px;
}
.cert-section-hint {
    margin-top: 15px; padding: 12px 15px;
    background: #fff3cd; border-radius: 8px;
    border: 1px solid #ffc107;
    font-size: 13px; color: #856404;
    display: flex; align-items: center; gap: 8px;
}
.cert-section-hint i { font-size: 16px; }

/* Modals */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.modal-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5); display: flex;
    align-items: center; justify-content: center;
    z-index: 99999; backdrop-filter: blur(3px);
}
.modal-container {
    background: #ffffff; border-radius: 14px;
    max-width: 700px; width: 95%; max-height: 90vh;
    overflow: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}
.modal-sm { max-width: 480px; }
.modal-lg { max-width: 850px; }
.modal-header {
    background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
    padding: 18px 20px; display: flex;
    justify-content: space-between; align-items: center;
    border-radius: 14px 14px 0 0;
}
.items-header {
    background: linear-gradient(135deg, #6f42c1 0%, #5a32a3 100%) !important;
}
.modal-header h3 {
    margin: 0; color: white; font-size: 18px;
    font-weight: 600; display: flex; align-items: center; gap: 8px;
}
.danger-header { background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%) !important; }
.modal-close {
    background: none; border: none; color: white;
    font-size: 26px; cursor: pointer;
    width: 30px; height: 30px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%; transition: background 0.2s;
}
.modal-close:hover { background: rgba(255,255,255,0.2); }
.modal-body { padding: 20px; color: #2c3e50; }
.modal-footer {
    padding: 14px 20px; background: #f8f9fa;
    border-top: 1px solid #e9ecef;
    display: flex; gap: 10px; justify-content: flex-end;
}
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
.form-group { margin-bottom: 0; }
.full-width { grid-column: 1 / -1; }
.form-label {
    display: block; font-size: 13px; font-weight: 600;
    color: #495057; margin-bottom: 5px;
}
.form-select {
    width: 100%; padding: 10px 12px;
    border: 2px solid #dee2e6; border-radius: 8px;
    background: white; font-size: 14px; color: #2c3e50;
    cursor: pointer; transition: border-color 0.2s;
}
.form-select:focus { outline: none; border-color: #1a73e8; box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.1); }
.confirm-text { text-align: center; font-size: 15px; color: #2c3e50; margin: 10px 0; }
.confirm-text strong { color: #dc3545; }

.toast-notif {
    position: fixed; top: 25px; right: 25px;
    padding: 12px 20px; border-radius: 10px;
    display: flex; align-items: center; gap: 8px;
    font-size: 14px; font-weight: 500;
    z-index: 100000; cursor: pointer;
    box-shadow: 0 4px 15px rgba(0,0,0,0.15); color: white;
}
.toast-notif.success { background: #28a745; }
.toast-notif.error { background: #dc3545; }
.notif-fade-enter-active, .notif-fade-leave-active { transition: all 0.3s ease; }
.notif-fade-enter, .notif-fade-leave-to { opacity: 0; transform: translateX(50px); }

@media (max-width: 768px) {
    .form-grid { grid-template-columns: 1fr; }
    .top-bar { flex-direction: column; align-items: flex-start; }
    .top-bar-right { width: 100%; }
    .filter-bar { flex-wrap: wrap; }
    .filter-select { min-width: auto; flex: 1; }
    .items-toolbar { flex-direction: column; align-items: stretch; }
}
</style>